import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, TimelineMax} from "gsap";
import {RoadKill} from "../RoadKill";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {BasicBitmapCountUp} from "../component/BasicBitmapCountUp";
import {RoadKillGameAssets} from "../RoadKillGameAssets";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {RKHelper} from "../utils/RKHelper";
import {RoadKillSound} from "../sound/RoadKillSound";
import {RoadKillGameMode} from "../server/RoadKillGameMode";
import {ISpecialSymData} from "../server/RoadKillData";
import {RoadKillGameConfig} from "../RoadKillGameConfig";

export class RoadKillFreespinOutroView extends PIXI.Container implements StageLayer {
    private _button: ContinueButton;
    private _controller: Function | null;
    private _youWon: PIXI.Text;
    private _countUp: BasicBitmapCountUp;
    private _fsOutroSpine: PIXI.spine.Spine;
    private _sfsOutroSpine: PIXI.spine.Spine;
    private _freespinsPlayed: PIXI.Text;
    private _totalHits: PIXI.Text;
    private _darkRespinGraphics: PIXI.Graphics;
    private _respinTotalWinText: PIXI.Text;

    constructor() {
        super();
        this.name = "freespinOutroView";
        this.init();
    }

    public onResize() {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
        this.setDarkRespinGraphicsProp();
    }

    public showOutroView(controller: Function): TimelineMax {
        const tl: gsap.core.Timeline = new gsap.core.Timeline({
                onStart: () => {
                    this.visible = true;
                    if (RoadKill.model.gameData.mode !== RoadKillGameMode.RESPIN) {
                        // no use if mode is respin instead of FS or SFS.
                        this._youWon.alpha = 0;
                        this._youWon.visible = !BelowStakeWinRestriction.isWinBelowStake() && (RoadKill.model.accumulatedRoundWin > 0);
                        this._button.alpha = 0;
                        this._button.visible = true;
                        if (RoadKill.model.mode === RoadKillGameMode.FREESPIN) {
                            this._fsOutroSpine.visible = true;
                        } else {
                            this._sfsOutroSpine.visible = true;
                        }
                        this._freespinsPlayed.alpha = 0;
                        this._freespinsPlayed.visible = true;
                        this._totalHits.alpha = 0;
                        this._totalHits.visible = true;
                    }
                    this._darkRespinGraphics.alpha = 0;
                    this._darkRespinGraphics.visible = true;
                    this._respinTotalWinText.alpha = 0;
                    this._respinTotalWinText.visible = true;
                    this._countUp.alpha = 0;
                    this._countUp.visible = true;
                    this._countUp.getCountAnimation(RoadKill.model.accumulatedRoundWin, RoadKill.model.accumulatedRoundWin, 0.01);
                    RoadKill.keypadPlugin.setWin(RoadKill.model.accumulatedRoundWin, true, BelowStakeWinRestriction.isWinBelowStake());
                }
            }),
            mode: string = RoadKill.model.mode,
            totalHits: number = RoadKill.model.gameData.totalCarsStopped,
            totalSpinsPlayed: number = RoadKill.model.gameData.respinIndex,
            animName: string = mode === RoadKillGameMode.FREESPIN ? "fsOutroIn" : "sfsOutroIn",
            animDurr: number = mode === RoadKillGameMode.FREESPIN ? this._fsOutroSpine.skeleton.data.findAnimation("fsOutroIn").duration : this._sfsOutroSpine.skeleton.data.findAnimation("sfsOutroIn").duration;

        this._controller = controller;
        if (mode === RoadKillGameMode.RESPIN) {
            // only for respin outro
            if (RoadKill.model.accumulatedRoundWin > 0) {
                tl.to(this, {duration: 0.5});
                tl.to([this._countUp, this._darkRespinGraphics, this._respinTotalWinText], {
                    alpha: 1,
                    duration: RoadKillGameConfig.HIDE_AND_ALPHA_DURATION,
                    ease: "Power3.easeIn"
                });
                tl.add(() => {
                    RoadKill.soundPlayer.stopTrack(RoadKillSound.RESPIN_BG_MUSIC);
                    RoadKill.soundPlayer.playTrack(RoadKillSound.RESPIN_END);
                }, "<+=0.2")
                tl.to(this._darkRespinGraphics, {
                    alpha: 0.5,
                    duration: RoadKillGameConfig.HIDE_AND_ALPHA_DURATION,
                    ease: "Power3.easeIn"
                }, "<");
                tl.to(this, {duration: 0.5});
                tl.to([this._countUp, this._darkRespinGraphics, this._respinTotalWinText], {
                    alpha: 0,
                    duration: RoadKillGameConfig.HIDE_AND_ALPHA_DURATION,
                    ease: "Power3.easeIn"
                });
                tl.to(this._darkRespinGraphics, {
                    alpha: 0,
                    duration: RoadKillGameConfig.HIDE_AND_ALPHA_DURATION,
                    ease: "Power3.easeIn"
                }, "<");

            }
            tl.add(() => {
                if (this._controller) {
                    this._controller();
                }
                this._respinTotalWinText.visible = false;
                this._countUp.visible = false;
                this._darkRespinGraphics.visible = false;
            })
            tl.add(() => {
                RoadKill.soundPlayer.stopTrack(RoadKillSound.RESPIN_BG_MUSIC);
                RoadKill.soundPlayer.fadeMusic(1, 400);
                RoadKill.soundPlayer.switchMusic(RoadKillGameMode.NORMAL);
            })
            return tl;
        }
        tl.add(() => {
            this._freespinsPlayed.text = RoadKill.apiPlugin.translations.translate("Spins played: ").concat(totalSpinsPlayed.toString());
            this._freespinsPlayed.position.x = 360;

            this._totalHits.text = RoadKill.apiPlugin.translations.translate("Total Hits: ") + totalHits;
            this._totalHits.position.x = 360;

            RoadKill.soundPlayer.fadeMusic(0, 200);
            RoadKill.soundPlayer.playTrack(RoadKillSound.FS_SFS_END_LOOP, 1, Number.MAX_VALUE);
            RoadKill.soundPlayer.playTrack(mode === RoadKillGameMode.FREESPIN ? RoadKillSound.FS_OUTRO_END : RoadKillSound.SFS_OUTRO_END, 1);
        })
        tl.add(RKHelper.playSpineWithTimeline(mode === RoadKillGameMode.FREESPIN ? this._fsOutroSpine : this._sfsOutroSpine, 0, animName));
        tl.to([this._countUp, this._totalHits, this._freespinsPlayed, this._youWon, this._button], {
            alpha: 1,
            duration: animDurr,
            ease: "Power3.easeIn"
        }, "<");
        tl.add(() => {
            this._button.enable(true);
        })
        return tl;
    }

    private calculateTotalHits(): number {
        const animalData: ISpecialSymData[] = RoadKill.model.gameData.specialSymbolList;
        let returnVal: number = 0;
        for (let idx: number = 0; idx < animalData.length; idx++) {
            returnVal += animalData[idx].getHitCount;
        }
        return returnVal;
    }

    private createFreespinsPlayedText(): PIXI.Text {
        const fsPlayedText: PIXI.Text = new PIXI.Text(RoadKill.apiPlugin.translations.translate("Spins played: "), new PIXI.TextStyle({
            fill: "#FFFFFF",
            fontFamily: "Helvetica",
            wordWrap: false,
            fontSize: 24,
            fontWeight: "bold",
            stroke: "#000000",
            strokeThickness: 2,
            dropShadow: true,
            dropShadowAngle: 120,
            dropShadowColor: "#000000",
            dropShadowDistance: 3,
        }));
        fsPlayedText.name = "fsPlayed";
        fsPlayedText.anchor.set(0.5);
        fsPlayedText.visible = false;
        fsPlayedText.position.set(360, 450);
        return fsPlayedText;
    }

    private createTotalHitsText(): PIXI.Text {
        const totalHitsText: PIXI.Text = new PIXI.Text(RoadKill.apiPlugin.translations.translate("Total Hits: "), new PIXI.TextStyle({
            fill: "#FFFFFF",
            fontFamily: "Helvetica",
            wordWrap: false,
            fontSize: 24,
            fontWeight: "bold",
            stroke: "#000000",
            strokeThickness: 2,
            dropShadow: true,
            dropShadowAngle: 120,
            dropShadowColor: "#000000",
            dropShadowDistance: 3,
        }));
        totalHitsText.name = "totalHits";
        totalHitsText.anchor.set(0.5);
        totalHitsText.visible = false;
        totalHitsText.position.set(360, 495);
        return totalHitsText;
    }

    private createDarRespinGraphics(): PIXI.Graphics {
        const graphics: PIXI.Graphics = new PIXI.Graphics();
        graphics.visible = false;
        graphics.alpha = 0;
        graphics.name = "darkRespinGraphics";
        return graphics;
    }

    private setDarkRespinGraphicsProp(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        this._darkRespinGraphics.clear();
        this._darkRespinGraphics.beginFill(0x000000, 1);
        this._darkRespinGraphics.drawRect(bounds.left, bounds.top, bounds.width, bounds.height);
        this._darkRespinGraphics.endFill();
    }

    private init(): void {
        this._youWon = this.createYouWon();
        this._countUp = this.createCountUp();
        this._button = this.createButton();
        this._fsOutroSpine = this.createFsOutroSpine();
        this._sfsOutroSpine = this.createSfsOutroSpine();
        this._freespinsPlayed = this.createFreespinsPlayedText();
        this._darkRespinGraphics = this.createDarRespinGraphics();
        this.setDarkRespinGraphicsProp();
        this._respinTotalWinText = this.createRespinTotalWinText();
        this._totalHits = this.createTotalHitsText();
        this._button.addClickCallback(() => {
            this.onButtonClick()
        });
        this.addChild(this._darkRespinGraphics, this._sfsOutroSpine, this._fsOutroSpine, this._totalHits, this._freespinsPlayed, this._respinTotalWinText, this._countUp, this._youWon, this._button);
    }

    private createRespinTotalWinText(): PIXI.Text {
        const text: PIXI.Text = new PIXI.Text(RoadKill.apiPlugin.translations.translate("Total Win"), new PIXI.TextStyle({
            fill: "#FFE930",
            fontFamily: "Helvetica",
            wordWrap: false,
            fontSize: 50,
            fontWeight: "bold",
            stroke: "#000000",
            strokeThickness: 2,
            dropShadow: true,
            dropShadowAngle: 120,
            dropShadowColor: "#000000",
            dropShadowDistance: 4,
        }));
        text.alpha = 0;
        text.position.set(360, 250);
        text.anchor.set(0.5);
        text.visible = false;
        return text;
    }

    private createFsOutroSpine(): PIXI.spine.Spine {
        const spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("fsOutro"));
        spine.position.set(355, 360);
        spine.visible = false;
        return spine;
    }

    private createSfsOutroSpine(): PIXI.spine.Spine {
        const spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("sfsOutro"));
        spine.position.set(355, 360);
        spine.visible = false;
        return spine;
    }

    private createYouWon(): PIXI.Text {
        const youWon: PIXI.Text = new PIXI.Text(RoadKill.apiPlugin.translations.translate("YOU WON"), new PIXI.TextStyle({
            fill: "#FFE930",
            fontFamily: "Helvetica",
            wordWrap: false,
            fontSize: 36,
            fontWeight: "bold",
            stroke: "#000000",
            strokeThickness: 2,
            dropShadow: true,
            dropShadowAngle: 120,
            dropShadowColor: "#000000",
            dropShadowDistance: 4,
        }));
        youWon.name = "you Won";
        youWon.anchor.set(0.5);
        youWon.visible = false;
        youWon.position.set(360, 268);
        return youWon;
    }

    private createCountUp(): BasicBitmapCountUp {
        const countUp: BasicBitmapCountUp = new BasicBitmapCountUp({
            fontName: RoadKillGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 40,
            tint: 0xffffff
        }, 720);
        countUp.scale.set(2);
        countUp.stopAt(0);
        countUp.position.set(360, 360);
        countUp.visible = false;
        return countUp;
    }

    private createButton(): ContinueButton {
        const continueBtn: ContinueButton = new ContinueButton();
        continueBtn.position.set(360, 615);
        continueBtn.pivot.set(continueBtn.width * 0.5, continueBtn.height * 0.5);
        continueBtn.enable(false);
        continueBtn.visible = false;
        return continueBtn;
    }

    private onButtonClick(): void {
        this._button.enable(false);
        const tl: TimelineMax = new TimelineMax({
                onComplete: () => {
                    this.visible = false;
                    this._fsOutroSpine.visible = false;
                    this._sfsOutroSpine.visible = false;
                    this._button.visible = false;
                    this._countUp.visible = false;
                    this._totalHits.visible = false;
                    this._freespinsPlayed.visible = false;
                    if (this._controller) {
                        this._controller();
                    }
                    this._controller = null;
                }
            }),
            animationName: string = RoadKill.model.mode === RoadKillGameMode.FREESPIN ? "fsOutroOut" : "sfsOutroOut",
            animationDurr: number = RoadKill.model.mode === RoadKillGameMode.SUPER_FREESPIN ? this._sfsOutroSpine.skeleton.data.findAnimation("sfsOutroOut").duration : this._fsOutroSpine.skeleton.data.findAnimation("fsOutroOut").duration;
        this._youWon.visible = false;
        tl.add(() => {
            RoadKill.soundPlayer.fadeMusic(1, 200);
            RoadKill.soundPlayer.switchMusic(RoadKillGameMode.NORMAL);
            RoadKill.soundPlayer.stopTrack(RoadKillSound.FS_SFS_END_LOOP);
            RoadKill.soundPlayer.stopTrack(RoadKill.model.mode === RoadKillGameMode.FREESPIN ? RoadKillSound.FS_OUTRO_END : RoadKillSound.SFS_OUTRO_END);
            RoadKill.soundPlayer.playTrack(RoadKillSound.FS_SFS_CONTINUE_BUTTON);
        });
        tl.add(RKHelper.playSpineWithTimeline(RoadKill.model.mode === RoadKillGameMode.FREESPIN ? this._fsOutroSpine : this._sfsOutroSpine, 0, animationName));
        tl.to([this._youWon, this._totalHits, this._freespinsPlayed, this._countUp, this._button], {
            alpha: 0,
            duration: animationDurr
        }, "<");
    }
}